import React, {
  useState,
  useCallback,
  useEffect,
  lazy,
  Suspense,
} from "react";

import TypeWriterText from "../../components/common/TypeWriterText";
import HomeImage from "../../assets/Group 109.png";
import RotatingBadge from "./RotatingBadge";
import {
  Section,
  Container,
  Box,
  HomeImages,
  Wave,
} from "./Home.styles";

// ─── Constants ─────────────────────────────────────────────────────────────────

/** localStorage key — centralised to avoid typo bugs */
const POPUP_SEEN_KEY = "hasSeenFreeTrialPopup";

/** Delay (ms) before the popup appears so the user sees the page first */
const POPUP_DELAY_MS = 2500;

// ─── Lazy-loaded Popup ─────────────────────────────────────────────────────────
// Excluded from the initial bundle — it's shown at most once per user ever.

const Popup = lazy(() => import("./FreeTrial/Popup"));

// ─── localStorage helper ───────────────────────────────────────────────────────
// Wrapped in try/catch: localStorage throws in private/incognito mode
// or when the browser has strict storage settings enabled.

const storage = {
  get: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Silently fail — popup will re-appear next visit but won't crash the app
    }
  },
};

// ─── Component ─────────────────────────────────────────────────────────────────

const Home = () => {
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    // Only show the popup if the user hasn't seen it before
    if (storage.get(POPUP_SEEN_KEY)) return;

    // Delay appearance so the user can see the page first
    const timer = setTimeout(() => setShowPopup(true), POPUP_DELAY_MS);
    return () => clearTimeout(timer); // clean up if component unmounts early
  }, []);

  // Write the seen-flag and close — useCallback keeps the reference stable
  const handlePopupClose = useCallback(() => {
    storage.set(POPUP_SEEN_KEY, "true");
    setShowPopup(false);
  }, []);

  return (
    <>
      <Section id="home">
        <Container>
          {/* Text content */}
          <Box>
            <TypeWriterText />
          </Box>

          {/* Hero image — loading="lazy" defers fetch on mobile */}
          <Box>
            <HomeImages
              src={HomeImage}
              alt="Yoga hero illustration"
              loading="lazy"
            />
          </Box>

          {/* Rotating badge — scrolls to the next section on click */}
          <RotatingBadge targetId="about" />
        </Container>

      </Section>

      {/*
       * Suspense wraps the lazy Popup.
       * fallback={null} renders nothing while the chunk loads
       * (acceptable since the popup is non-critical UI).
       */}
      {showPopup && (
        <Suspense fallback={null}>
          <Popup onClose={handlePopupClose} />
        </Suspense>
      )}
    </>
  );
};

export default React.memo(Home);